import { searchMemoryAction } from '@/app/actions/memory/search';
import { NodeLayout } from '@/components/nodes/layout';
import { Button } from '@/components/ui/button';
import {
  AIMessage,
  AIMessageContent,
} from '@/components/ui/kibo-ui/ai/message';
import { AIResponse } from '@/components/ui/kibo-ui/ai/response';
import {
  AISource,
  AISources,
  AISourcesContent,
  AISourcesTrigger,
} from '@/components/ui/kibo-ui/ai/source';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAnalytics } from '@/hooks/use-analytics';
import { useReasoning } from '@/hooks/use-reasoning';
import { handleError } from '@/lib/error/handle';
import { textModels } from '@/lib/models/text';
import {
  getDescriptionsFromImageNodes,
  getFilesFromFileNodes,
  getImagesFromImageNodes,
  getMemoryNodeIds,
  getTextFromTextNodes,
  getTranscriptionFromAudioNodes,
  getTweetContentFromTweetNodes,
  hasMemoryNodes,
} from '@/lib/xyflow';
import { useProject } from '@/providers/project';
import { ReasoningTunnel } from '@/tunnels/reasoning';
import { useChat } from '@ai-sdk/react';
import { getIncomers, useReactFlow } from '@xyflow/react';
import { ClockIcon, PlayIcon, RotateCcwIcon, SquareIcon } from 'lucide-react';
import {
  type ChangeEventHandler,
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { mutate } from 'swr';
import type { TextNodeProps } from '.';
import { ModelSelector } from '../model-selector';

type TextTransformProps = TextNodeProps & {
  title: string;
};

const getDefaultModel = (models: typeof textModels) => {
  const defaultModel = models
    .flatMap((model) => model.models)
    .find((model) => model.default);

  if (!defaultModel) {
    throw new Error('No default model found');
  }

  return defaultModel;
};

export const TextTransform = ({
  data,
  id,
  type,
  title,
}: TextTransformProps) => {
  const { updateNodeData, getNodes, getEdges } = useReactFlow();
  const project = useProject();
  const modelId = data.model ?? getDefaultModel(textModels).id;
  const analytics = useAnalytics();
  const [reasoning, setReasoning] = useReasoning();
  const { append, messages, setMessages, status, stop } = useChat({
    body: {
      modelId,
    },
    onError: (error) => handleError('Error generating text', error),
    onFinish: (message) => {
      updateNodeData(id, {
        generated: {
          text: message.content,
          sources:
            message.parts?.filter((part) => part.type === 'source') ?? [],
        },
        updatedAt: new Date().toISOString(),
      });

      setReasoning((oldReasoning) => ({
        ...oldReasoning,
        isGenerating: false,
      }));

      toast.success('Text generated successfully');

      setTimeout(() => mutate('credits'), 5000);
    },
  });

  const handleGenerate = useCallback(async () => {
    const incomers = getIncomers({ id }, getNodes(), getEdges());
    const textPrompts = getTextFromTextNodes(incomers);
    const audioPrompts = getTranscriptionFromAudioNodes(incomers);
    const images = getImagesFromImageNodes(incomers);
    const imageDescriptions = getDescriptionsFromImageNodes(incomers);
    const tweetContent = getTweetContentFromTweetNodes(incomers);
    const files = getFilesFromFileNodes(incomers);

    // Check for any valid input sources
    const hasValidInput =
      textPrompts.length > 0 ||
      audioPrompts.length > 0 ||
      hasMemoryNodes(incomers) ||
      data.instructions;

    if (!hasValidInput) {
      handleError(
        'Error generating text',
        'No input found from connected nodes or instructions'
      );
      return;
    }

    const content: string[] = [];

    if (data.instructions) {
      content.push('--- Instructions ---', data.instructions);
    }

    // Handle memory nodes - both search and provide IDs
    if (hasMemoryNodes(incomers)) {
      const memoryIds = getMemoryNodeIds(incomers);

      if (memoryIds.length > 0) {
        console.log(
          '🧠 Memory nodes detected, using memory IDs and searching content:',
          memoryIds
        );

        // Add memory IDs as reference
        const memoryIdText = `Memory IDs: ${memoryIds.join(', ')}`;
        content.push('--- Memory References ---', memoryIdText);
      }

      const { memories, error, errorMessage } = await searchMemoryAction({
        query: content.join('\n'),
        projectId: project?.id,
        limit: 5,
      });

      if (error) {
        handleError('Error searching memories', errorMessage);
        return;
      }

      if (memories.length > 0) {
        content.push(
          '--- Memory Content ---',
          memories
            .map((memory) =>
              memory.chunks
                .map((chunk) => chunk.content)
                .join('\n\n')
                .replace(/^/gm, '  ')
            )
            .join('\n')
        );
      }

      console.log('🔍 Search result:', memories);
    }

    if (textPrompts.length) {
      content.push('--- Text Prompts ---', ...textPrompts);
    }

    if (audioPrompts.length) {
      content.push('--- Audio Prompts ---', ...audioPrompts);
    }

    if (imageDescriptions.length) {
      content.push('--- Image Descriptions ---', ...imageDescriptions);
    }

    if (tweetContent.length) {
      content.push('--- Tweet Content ---', ...tweetContent);
    }

    analytics.track('canvas', 'node', 'generate', {
      type,
      promptLength: content.join('\n').length,
      model: modelId,
      instructionsLength: data.instructions?.length ?? 0,
      imageCount: images.length,
      fileCount: files.length,
    });

    setMessages([]);
    append({
      role: 'user',
      content: content.join('\n'),
      experimental_attachments: [
        ...images.map((image) => ({
          url: image.url,
          contentType: image.type,
        })),
        ...files.map((file) => ({
          url: file.url,
          contentType: file.type,
          name: file.name,
        })),
      ],
    });
  }, [
    append,
    data.instructions,
    getEdges,
    getNodes,
    id,
    modelId,
    type,
    analytics.track,
    setMessages,
    project?.id,
  ]);

  const handleInstructionsChange: ChangeEventHandler<HTMLTextAreaElement> = (
    event
  ) => updateNodeData(id, { instructions: event.target.value });

  const toolbar = useMemo(() => {
    const items: ComponentProps<typeof NodeLayout>['toolbar'] = [];

    items.push({
      children: (
        <ModelSelector
          value={modelId}
          options={textModels}
          key={id}
          className="w-[200px] rounded-full"
          onChange={(value) => updateNodeData(id, { model: value })}
        />
      ),
    });

    if (status === 'submitted' || status === 'streaming') {
      items.push({
        tooltip: 'Stop',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            onClick={stop}
            disabled={!project?.id}
          >
            <SquareIcon size={12} />
          </Button>
        ),
      });
    } else if (messages.length || data.generated?.text) {
      items.push({
        tooltip: 'Regenerate',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            onClick={handleGenerate}
            disabled={!project?.id}
          >
            <RotateCcwIcon size={12} />
          </Button>
        ),
      });
    } else {
      items.push({
        tooltip: 'Generate',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            onClick={handleGenerate}
            disabled={!project?.id}
          >
            <PlayIcon size={12} />
          </Button>
        ),
      });
    }

    if (data.updatedAt) {
      items.push({
        tooltip: `Last updated: ${new Intl.DateTimeFormat('en-US', {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(new Date(data.updatedAt))}`,
        children: (
          <Button size="icon" variant="ghost" className="rounded-full">
            <ClockIcon size={12} />
          </Button>
        ),
      });
    }

    return items;
  }, [
    data.generated?.text,
    data.updatedAt,
    handleGenerate,
    updateNodeData,
    modelId,
    id,
    messages,
    project?.id,
    status,
    stop,
  ]);

  const nonUserMessages = messages.filter((message) => message.role !== 'user');

  useEffect(() => {
    const hasReasoning = messages.some((message) =>
      message.parts.some((part) => part.type === 'reasoning')
    );

    if (hasReasoning && !reasoning.isReasoning && status === 'streaming') {
      setReasoning({ isReasoning: true, isGenerating: true });
    }
  }, [messages, reasoning, status, setReasoning]);

  return (
    <NodeLayout id={id} data={data} title={title} type={type} toolbar={toolbar}>
      {status === 'submitted' ? (
        <div className="p-4">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-60 animate-pulse rounded-lg" />
            <Skeleton className="h-4 w-40 animate-pulse rounded-lg" />
            <Skeleton className="h-4 w-50 animate-pulse rounded-lg" />
          </div>
        </div>
      ) : (
        <div className="nowheel h-full max-h-[30rem] flex-1 overflow-auto rounded-t-3xl rounded-b-xl bg-secondary p-4">
          {data.generated?.text && !nonUserMessages.length && (
            <ReactMarkdown>{data.generated.text}</ReactMarkdown>
          )}
          {!data.generated?.text && !nonUserMessages.length && (
            <div className="flex aspect-video w-full items-center justify-center bg-secondary">
              <p className="text-muted-foreground text-sm">
                Press <PlayIcon size={12} className="-translate-y-px inline" />{' '}
                to generate text
              </p>
            </div>
          )}
          {Boolean(nonUserMessages.length) &&
            nonUserMessages.map((message) => (
              <AIMessage
                key={message.id}
                from={message.role === 'assistant' ? 'assistant' : 'user'}
                className="p-0 [&>div]:max-w-none"
              >
                <div>
                  {message.parts.filter((part) => part.type === 'source')
                    ?.length > 0 && (
                    <AISources>
                      <AISourcesTrigger
                        count={
                          message.parts.filter((part) => part.type === 'source')
                            .length
                        }
                      />
                      <AISourcesContent>
                        {message.parts
                          .filter((part) => part.type === 'source')
                          .map(({ source }) => (
                            <AISource
                              key={source.url}
                              href={source.url}
                              title={
                                source.title ?? new URL(source.url).hostname
                              }
                            />
                          ))}
                      </AISourcesContent>
                    </AISources>
                  )}
                  <AIMessageContent className="bg-transparent p-0">
                    <AIResponse>{message.content}</AIResponse>
                  </AIMessageContent>
                </div>
              </AIMessage>
            ))}
        </div>
      )}
      <Textarea
        value={data.instructions ?? ''}
        onChange={handleInstructionsChange}
        placeholder="Enter instructions"
        className="shrink-0 resize-none rounded-none border-none bg-transparent! shadow-none focus-visible:ring-0"
      />
      <ReasoningTunnel.In>
        {messages.flatMap((message) =>
          message.parts
            .filter((part) => part.type === 'reasoning')
            .flatMap((part) =>
              part.details
                .filter((detail) => detail.type === 'text')
                .map((detail) => detail.text)
            )
        )}
      </ReasoningTunnel.In>
    </NodeLayout>
  );
};
